import React, { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import { Container, ShippingInfo, ItemsTable, ButtonPrimary } from 'components';
import { accountState, loadingState, authenticationState } from 'state/atoms';
import { storageCheckoutSelector, storageCurrentRmaSelector } from 'state/selectors';
import { defaultCheckoutState, defaultCurrentRmaState } from 'state/defaults';
import { CHECKOUT_REVIEW_STEP, CASE_SUBMIT_ERROR, FEE_NOT_ALLOWED_MESSAGE } from 'utils/constants';
import { getItemsPrice, getShippingPrice, getTotalCasePrice } from 'utils/helpers';
import { useNotifyError, useCheckoutStepsValidation } from 'utils/hooks';
import { notifyCaseSubmitted } from 'connectors/bugsnag';
import { CREATE_CASE } from 'connectors/mutations/case';

import { Title, NavigationButtons, SubtotalBlock, TotalBlock } from './styledComponents';
import { getCreateCaseVariables } from './helpers';
import { useFormatPrice } from 'utils/hooks/useFormatPrice';

const CheckoutReview = () => {
  const [checkout, setCheckout] = useRecoilState(storageCheckoutSelector);
  const [{ items }, setCurrentRma] = useRecoilState(storageCurrentRmaSelector);
  const validatePreviousSteps = useCheckoutStepsValidation(CHECKOUT_REVIEW_STEP);
  const setLoading = useSetRecoilState(loadingState);

  const accountData = useRecoilValue(accountState);
  const { endCustomerAccountId, hasAllowFeePermission } = useRecoilValue(authenticationState);
  const formatPrice = useFormatPrice();

  const history = useHistory();

  const notifyError = useNotifyError({
    fallbackMessage:
      'Error: RMA submission failed. Please, contact RMA team at RMA@calix.com if this problem persists.',
  });
  const notifyFeePermissionError = useNotifyError({
    fallbackMessage: FEE_NOT_ALLOWED_MESSAGE,
  });
  const [createCase, { loading }] = useMutation(CREATE_CASE);

  const billToAddress = accountData.getDefaultBillToAddress;
  const itemsPrice = getItemsPrice(items);
  const totalPrice = getTotalCasePrice(
    items,
    checkout.shipping.method,
    billToAddress,
    accountData.currencyConversionRate
  );

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  useEffect(() => {
    validatePreviousSteps().then((stepsAreValid) => {
      if (stepsAreValid && checkout.currentStep !== CHECKOUT_REVIEW_STEP) {
        setCheckout({
          ...checkout,
          currentStep: CHECKOUT_REVIEW_STEP,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackClick = () => {
    setCheckout({
      ...checkout,
      billing: {
        ...checkout.billing,
        touched: false,
      },
    });
    history.push('/cases/new/checkout/billing');
  };

  const parseErrorMessage = (message) => {
    let parsedValue = message;
    try {
      parsedValue = JSON.parse(message);
    } catch (error) {}
    return parsedValue;
  };
  const allowRetry = (error) => {
    const errorMessage = parseErrorMessage(error?.message || '{}');
    return errorMessage.errorCode === CASE_SUBMIT_ERROR.UNABLE_TO_LOCK_ERROR_CODE;
  };

  const handleCaseSubmit = ({ attemptsCounter = 0 }) => {
    const { variables } = getCreateCaseVariables({
      items,
      checkout,
      totalPrice,
      accountId: endCustomerAccountId,
      currencyCode: accountData.defaultCurrencyCode,
    });

    const repairFeeNotAllowed =
      items.some((item) => item.acceptedCharges && item.initialPrice > 0) && !hasAllowFeePermission;

    if (repairFeeNotAllowed) {
      return notifyFeePermissionError();
    }

    createCase({ variables })
      .then(({ data }) => {
        notifyCaseSubmitted(variables.case);

        setCheckout(defaultCheckoutState);
        setCurrentRma({
          ...defaultCurrentRmaState,
          accountId: endCustomerAccountId,
          caseNumber: data.createRmaCase.caseNumber,
        });
        history.push('/cases/new/confirmation');
      })
      .catch((error) => {
        if (allowRetry(error) && attemptsCounter < 5) {
          attemptsCounter++;
          handleCaseSubmit({ attemptsCounter });
        } else {
          notifyError(error.networkError ? {} : error);
        }
      });
  };

  return (
    <Container>
      <Title>Review Your Order</Title>
      <ShippingInfo
        shipping={{
          ...checkout.shipping,
          ...checkout.shipping.address,
        }}
        poNumber={checkout.billing.poNumber}
      />
      <ItemsTable
        showTotals
        items={items}
        shippingPrice={getShippingPrice(
          checkout.shipping.method,
          billToAddress,
          accountData.currencyConversionRate
        )}
        shippingMethod={checkout.shipping.method}
        currencyCode={accountData.defaultCurrencyCode}
      />
      <SubtotalBlock>
        <span>Sub-Total:</span>
        <span>{formatPrice(itemsPrice)}</span>
      </SubtotalBlock>
      <TotalBlock>
        <span>Total:</span>
        <span>{formatPrice(totalPrice)}</span>
      </TotalBlock>
      <NavigationButtons>
        <a
          href={process.env.REACT_APP_CALIX_RMA_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          By submitting the RMA Request, you agree to the terms of the Calix RMA Policy
        </a>
        <ButtonPrimary onClick={handleBackClick}>BACK</ButtonPrimary>
        <ButtonPrimary submit onClick={handleCaseSubmit}>
          Submit RMA
        </ButtonPrimary>
      </NavigationButtons>
    </Container>
  );
};

export { CheckoutReview };
